/**
 * AI CONTEXT: architecture - Product state management with API integration
 * Journal: J-20250809-01
 *
 * Purpose:
 * - Load products from REST API (API Gateway + Lambda + DynamoDB)
 * - Provide CRUD for admin with optimistic UI
 * - Fallback to static demo data if API is not configured
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { authorizedFetch, isApiConfigured } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProductContext = createContext();

export const useProducts = () => {
  const ctx = useContext(ProductContext);
  if (!ctx) throw new Error('useProducts must be used within a ProductProvider');
  return ctx;
};

const demoProducts = [
  { id: '1', name: 'Fluke Sticker', price: 20, salesPrice: 15, entries: 1, credits: 1, tier: 'Bronze', image: '/images/product-1.jpg', description: 'Premium quality tactical sticker', category: 'Bronze', inStock: true, rating: 4.5, reviews: 12 },
  { id: '2', name: 'Tactical Wristband', price: 40, entries: 2, credits: 1, tier: 'Silver', image: '/images/product-2.jpg', description: 'Durable tactical wristband', category: 'Silver', inStock: true, rating: 4.8, reviews: 8 }
];

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const authKey = user?.userId ?? user?.username ?? (user ? 'signed-in' : 'anonymous');
  // Always use GET /products; backend returns full list for admin JWT and filtered list for others
  const productsPath = '/products';

  // Validation function to check for duplicate display order
  const validateDisplayOrder = (displayOrder, excludeProductId = null) => {
    if (displayOrder === null || displayOrder === undefined || displayOrder === '') {
      return { isValid: true, message: null };
    }
    
    const orderNumber = parseInt(displayOrder);
    if (isNaN(orderNumber)) {
      return { isValid: false, message: 'Display order must be a valid number' };
    }
    
    const existingProduct = products.find(product => 
      product.displayOrder === orderNumber && 
      product.id !== excludeProductId
    );
    
    if (existingProduct) {
      return { 
        isValid: false, 
        message: `Display order ${orderNumber} is already in use by "${existingProduct.name}". Please choose a different number or update the existing product.` 
      };
    }
    
    return { isValid: true, message: null };
  };

  // Helper function to get available display order numbers
  const getAvailableDisplayOrders = (excludeProductId = null) => {
    const usedOrders = products
      .filter(product => product.displayOrder !== null && product.displayOrder !== undefined && product.id !== excludeProductId)
      .map(product => product.displayOrder)
      .sort((a, b) => a - b);
    
    const availableOrders = [];
    let currentOrder = 1;
    
    // Find gaps in the sequence
    for (const usedOrder of usedOrders) {
      while (currentOrder < usedOrder) {
        availableOrders.push(currentOrder);
        currentOrder++;
      }
      currentOrder = usedOrder + 1;
    }
    
    // Add next few available numbers after the highest used number
    const maxUsed = usedOrders.length > 0 ? Math.max(...usedOrders) : 0;
    for (let i = 1; i <= 5; i++) {
      availableOrders.push(maxUsed + i);
    }
    
    return availableOrders.slice(0, 10); // Return up to 10 suggestions
  };

  // Refetch when auth or admin status changes: admin uses /admin/products (full list), others use /products (filtered)
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (isApiConfigured()) {
          const data = await authorizedFetch(productsPath, { method: 'GET' });
          setProducts(Array.isArray(data) ? data : []);
        } else {
          setProducts(demoProducts);
        }
      } catch (err) {
        console.warn('Failed to load products, using demo:', err);
        setProducts(demoProducts);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authKey]);

  const createProduct = async (draft) => {
    const normalized = { ...draft };
    if (normalized.credits === undefined || normalized.credits === null || Number(normalized.credits) <= 0) {
      normalized.credits = 1;
    }
    if (!normalized.image && Array.isArray(normalized.images) && normalized.images.length > 0) {
      normalized.image = normalized.images[0];
    }
    if (!normalized.image) {
      normalized.image = '/files/Logo.png';
    }
    const rawSc = normalized.shippingCost;
    const scNum = (rawSc !== undefined && rawSc !== null && rawSc !== '') ? Number(rawSc) : 5;
    normalized.shippingCost = Number.isFinite(scNum) ? Math.max(0, scNum) : 5;
    
    // Validate display order before creating
    const displayOrderValidation = validateDisplayOrder(normalized.displayOrder);
    if (!displayOrderValidation.isValid) {
      toast.error(displayOrderValidation.message);
      return { success: false, error: displayOrderValidation.message };
    }
    
    const optimistic = { id: `tmp-${Date.now()}`, ...normalized };
    setProducts(prev => [...prev, optimistic]);
    if (!isApiConfigured()) return toast.success('Product created (demo mode)');
    try {
      const created = await authorizedFetch('/products', { method: 'POST', body: JSON.stringify(normalized) });
      setProducts(prev => prev.map(p => (p.id === optimistic.id ? created : p)));
      toast.success('Product created');
      return { success: true };
    } catch (e) {
      setProducts(prev => prev.filter(p => p.id !== optimistic.id));
      toast.error('Failed to create product');
      return { success: false, error: 'Failed to create product' };
    }
  };

  const updateProduct = async (id, updates) => {
    const prev = products;
    const safeUpdates = { ...updates };
    if (safeUpdates.price !== undefined) safeUpdates.price = Number(safeUpdates.price) || 0;
    if (safeUpdates.salesPrice !== undefined) {
      // Treat salesPrice of 0 as null (no discount) to avoid $0 checkout bug
      const salesPriceValue = safeUpdates.salesPrice === null || safeUpdates.salesPrice === "" ? null : Number(safeUpdates.salesPrice);
      safeUpdates.salesPrice = (salesPriceValue && salesPriceValue > 0) ? salesPriceValue : null;  // AI CONTEXT: sales-pricing - Handle optional sales price
    }
    if (safeUpdates.entries !== undefined) safeUpdates.entries = parseInt(safeUpdates.entries) || 0;
    if (safeUpdates.credits !== undefined) {
      const parsedCredits = parseInt(safeUpdates.credits, 10);
      safeUpdates.credits = Number.isFinite(parsedCredits) && parsedCredits > 0 ? parsedCredits : 1;
    }
    if (safeUpdates.rating !== undefined) safeUpdates.rating = parseFloat(safeUpdates.rating) || 0;
    if (safeUpdates.reviews !== undefined) safeUpdates.reviews = parseInt(safeUpdates.reviews) || 0;
    if (safeUpdates.displayOrder !== undefined) safeUpdates.displayOrder = safeUpdates.displayOrder === null ? null : parseInt(safeUpdates.displayOrder) || null;
    if (safeUpdates.shippingCost !== undefined) {
      const sc = Number(safeUpdates.shippingCost);
      safeUpdates.shippingCost = Number.isFinite(sc) ? Math.max(0, sc) : 5;
    }
    if (!safeUpdates.image && Array.isArray(safeUpdates.images) && safeUpdates.images.length > 0) {
      safeUpdates.image = safeUpdates.images[0];
    }
    if (!safeUpdates.image && (!Array.isArray(safeUpdates.images) || safeUpdates.images.length === 0)) {
      safeUpdates.image = '/files/Logo.png';
    }
    
    // Validate display order before updating (exclude current product from check)
    const displayOrderValidation = validateDisplayOrder(safeUpdates.displayOrder, id);
    if (!displayOrderValidation.isValid) {
      toast.error(displayOrderValidation.message);
      return { success: false, error: displayOrderValidation.message };
    }
    
    setProducts(p => p.map(x => (x.id === id ? { ...x, ...safeUpdates } : x)));
    if (!isApiConfigured()) return toast.success('Product updated (demo mode)');
    try {
      const updated = await authorizedFetch(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(safeUpdates) });
      setProducts(p => p.map(x => (x.id === id ? updated : x)));
      toast.success('Product updated');
      return { success: true };
    } catch (e) {
      setProducts(prev);
      toast.error('Failed to update product');
      return { success: false, error: 'Failed to update product' };
    }
  };

  const deleteProduct = async (id) => {
    const prev = products;
    setProducts(p => p.filter(x => x.id !== id));
    if (!isApiConfigured()) return toast.success('Product deleted (demo mode)');
    try {
      await authorizedFetch(`/products/${id}`, { method: 'DELETE' });
      toast.success('Product deleted');
    } catch (e) {
      setProducts(prev);
      toast.error('Failed to delete product');
    }
  };

  return (
    <ProductContext.Provider value={{ 
      products, 
      loading, 
      createProduct, 
      updateProduct, 
      deleteProduct,
      validateDisplayOrder,
      getAvailableDisplayOrders
    }}>
      {children}
    </ProductContext.Provider>
  );
};

export default ProductContext;


